<template>
    <div>
        <box>
            <template v-slot:header>
                <h4>
                    <i class="fas fa-list"></i> {{resource('JourneyComposer_Overview_Active_Header')}}
                </h4>
            </template>

            <template v-slot:body>
                <div class="row mb-3">
                    <div class="col-6">
                        <a :href="journeyStatisticsUrl">
                            <button class="btn btn-outline-secondary pull-left">
                                <i class="fas fa-chart-area"></i> {{resource('JourneyComposer_Overview_JourneyReport_Button')}}
                            </button>
                        </a>
                    </div>
                    <div class="col-6">
                        <button class="btn btn-primary pull-right" @click="newJourney" v-if="hasWritePermission">
                            <i class="fas fa-plus"></i> {{resource('JourneyComposer_Overview_NewJourney_Button')}}
                        </button>
                    </div>
                </div>

                <ExpandableDataTable 
                    class="table table-striped"
                    :items="activeJourneys"
                    :headers="headers"
                    :focusFilterOnLoad="true"

                    @clone="prepareClone($event.id)"
                    @delete="prepareDelete($event.id)"
                    @edit="handleEdit($event.id)"
                    @statistics="navigateToStatistics($event.id)"
                >
                </ExpandableDataTable>
            </template>
        </box>
        
        <box>
            <template #header>
                <h4>
                    <i class="fas fa-list"></i> {{resource('JourneyComposer_Overview_Inactive_Header')}}
                </h4>
            </template>

            <template #body>
                <ExpandableDataTable 
                    class="table table-striped"
                    :items="inactiveJourneys"
                    :headers="headers"

                    @clone="prepareClone($event.id)"
                    @delete="prepareDelete($event.id)"
                    @edit="handleEdit($event.id)"
                    @statistics="navigateToStatistics($event.id)"
                >
                </ExpandableDataTable>
            </template>
        </box>


        <prompt v-if="showPrompt"
                :title="promptTitle"
                :body="promptBody"
                :size="promptsize"
                :icon="promptIcon"

                @yes="promptHandler"
                @hidden="showPrompt = false"
        />
    </div>
</template>

<script>

//Components
import ExpandableDataTable from './ExpandableDataTable/ExpandableDataTable.vue';
import BootstrapBox from '@core/Components/Util/BootstrapBox.vue';
import DataTable, { defaultContentType } from '@core/Components/Util/DataTable/DataTable.vue';
import Prompt, {sizes} from '@core/Components/Util/Prompt.vue';

//Mixins
import resourceMixin from '@core/Util/Mixins/resources';
import tooltipsMixin from '@core/Util/Mixins/tooltips';
import cloneJourneyMixin from '@modules/JourneyBuilder/Mixins/clone-journey'
import permissionsMixin from '@modules/JourneyBuilder/Mixins/journey-permissions'

//Util
import api, {controllerEndpoint} from '../../api'
import { bootstrapSuccessGrowl, bootstrapDangerGrowl } from '../../../../Util/growler';
import divisionsApi from "../../../../Api/divisions";

/**
 * Checks if the given journey is to be displayed as active or not
 * 
 * A journey is to be displayed as active when:
 * it is marked as active
 * it is an umbrella journey (= has children) with at least a single active child journey 
 * @param {Object} journey
 * @return {boolean}
 */
function isActive(journey){
    return journey.isActive || journey.children.some(c => c.isActive);
}

export default {
    name: 'journey-builder-overview',
    mixins: [resourceMixin, cloneJourneyMixin, permissionsMixin, tooltipsMixin],
    components: {
        'box': BootstrapBox,
        DataTable,
        Prompt,
        ExpandableDataTable
    },
    created() {
        this.currentJourneys = this.journeys;

        const divisionsPromise = divisionsApi.getDivisionsList({ includeDisabled: true }).then(
            async divisionList => this.divisionList = divisionList,
            bootstrapDangerGrowl
        );

        const worldsPromise = api.getWorlds().then(
            (worlds) => {
                this.worlds = worlds;
            },
            bootstrapDangerGrowl
        );
        
        //Our table has probably already had a render cycle prior to our worlds/divisions being available
        //trigger a re-render post load to ensure names are properly displayed
        Promise.all([divisionsPromise, worldsPromise]).finally(() => this.reloadTable());
    },
    methods: {
        reloadTable(){
            const table = this.$refs.table;
            if(table && table.reloadDataTable){
                table.reloadDataTable();
            }
        },
        handleEdit(id) {
            window.location.href = `${controllerEndpoint}Index/${id}`;
        },
        prepareDelete(id) {
            if(this.hasDeletePermission) {
                this.promptTitle = this.resource('JourneyBuilder_Overview_Delete_Prompt_Title');
                this.promptBody = this.resource('JourneyBuilder_Overview_Delete_Prompt_Body');
                this.promptIcon = 'far fa-trash-alt text-danger';
                
                this.promptHandler = () => {
                    this.handleDelete(id);
                }

                this.showPrompt = true;
            }
        },
        prepareClone(id) {
            if(this.canWrite) {
                this.promptTitle = this.resource('JourneyBuilder_Overview_Clone_Prompt_Title');
                this.promptBody = this.resource('JourneyBuilder_Overview_Clone_Prompt_Body');
                this.promptIcon = 'far fa-clone text-warning';
                
                this.promptHandler = () => {
                    this.cloneJourney(id);
                }
                
                this.showPrompt = true;
            }
        },
        handleDelete(id) {
            const vm = this;

            if(id && vm.hasDeletePermission) {
                api.deleteJourneyById(id).then(
                    () => {
                        vm.currentJourneys = vm.currentJourneys.filter((journey) =>
                            journey.id !== id
                        );

                        this.reloadTable();
                        //vm.$refs.table.reloadDataTable();
                        bootstrapSuccessGrowl(this.resource('JourneyBuilder_Overview_Delete_SuccessMessage'));
                    },
                    bootstrapDangerGrowl
                )
            }
        },
        navigateToStatistics(journeyId) {
            window.location.href = api.getStatisticsPageUrl(journeyId);
        },
        newJourney() {
            window.location.href = `${controllerEndpoint}Index`;
        }
    },
    data() {
        return {
            promptTitle: '',
            promptBody: '',
            promptIcon: '',
            currentJourneys: [],
            worlds: [],

            /**
             * @type {DivisionTree}
             */
            divisionList: undefined,
            showPrompt: false,
            journeyStatisticsUrl: api.getStatisticsPageUrl(),
            promptHandler: () => {}
        }
    },
    computed: {
        promptsize() {
            return sizes.medium;
        },
        divisionsNameMap(){
            const divisionsNameMap = new Map();

            let unprocessedDivisions = [];
            let currentDivision = this.divisionList;

            while(currentDivision){
                //It is possible for a division to have two parents
                //we only care about the first encounter
                if(divisionsNameMap.has(currentDivision.id)){
                    continue;
                }

                //Register division name
                divisionsNameMap.set(currentDivision.id, currentDivision.name);

                currentDivision.childDivisions.forEach(childDivision => {
                    if(!divisionsNameMap.has(childDivision.id)){
                        unprocessedDivisions.push(childDivision);
                    }
                });

                //Popping an empty array returns undefined
                //therefor we should just have to check for truthyness of our currentDivision
                currentDivision = unprocessedDivisions.pop();
            }
            
            return divisionsNameMap;
        },
        headers() {
            return [
                this.resource('JourneyModel_Name_DisplayName'),
                this.resource('JourneyModel_World_DisplayName'),
                this.resource('JourneyBuilderPublishModel_DivisionId_DisplayName'),
                this.resource('JourneyModel_Description_DisplayName'),
                this.resource('JourneyModel_Version_DisplayName'),
                // this.resource('JourneyModel_IsLocked_DisplayName'),
                this.resource('JourneyModel_IsPublished_DisplayName'),
                ''
            ];
        },
        mappedJourneys() {
            const vm = this;
            
            const mapJourney = (journey) => {
                const world = vm.worlds.find((world) => world.id === journey.worldId);
                const worldName = world ? world.name : '-';

                return {
                    id: journey.id,
                    name: journey.name,
                    world:  worldName,
                    division: this.divisionsNameMap.get(journey.divisionId) || '-',
                    description: journey.description,
                    version: journey.version,
                    // isLocked: journey.isLocked,
                    isPublished: journey.isPublished,
                    children: journey.children,
                    isActive: journey.isActive,
                    visible: true
                };
            };

            return vm.currentJourneys.map((journey) => {
                const mapped = mapJourney(journey);
                mapped.children = mapped.children.map((child) => mapJourney(child));
                return mapped;
            });
        },
        activeJourneys(){
            return this.mappedJourneys.filter(j => isActive(j));
        },
        inactiveJourneys(){
            return this.mappedJourneys.filter(j => !isActive(j));
        }
    },
    props: {
        journeys: {
            type: Array,
            required: true
        }
    }
};
</script>

<style lang="scss" scoped>

.btn.btn-primary.pull-right {
    margin-right: 15px;
}

</style>